import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { PrcVersionSummary } from '@shared/api';
import { FleetChip, ScrollRegion, fleetLeading, fleetType, formatCount } from '@entities/fleet';
import {
  PRC_COMMON_LABEL,
  formatIsoDate,
  prcSourceLabel,
  prcStatusLabel,
  prcStatusTone,
} from '@entities/prc';
import { MODELS, count, formatDay, personName } from '../format';
import { NARROW, NumTable } from '../ui';

/**
 * Версии расчёта ПРЦ. Широкий контейнер — таблица, узкий — строки-карточки:
 * название и статус, ниже базовый год, модели, кто создал и утвердил.
 */

const Table = styled(NumTable)`
  td.c-name {
    min-width: 200px;
  }

  td.c-person {
    min-width: 120px;
  }

  td.c-objects {
    min-width: 140px;
    max-width: 240px;
    overflow-wrap: anywhere;
  }

  tr.row {
    cursor: pointer;
  }

  .narrow-only {
    display: none;
  }

  @container prc (max-width: ${NARROW}) {
    &,
    & > tbody,
    & tr,
    & td {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    .narrow-only {
      display: block;
    }

    tr.row {
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    tr.row > td {
      padding: 0;
      border: none;
      min-width: 0;
      background: none;
    }
  }
`;

const NameLink = styled(Link)`
  display: block;
  font-size: ${fleetType.base};
  font-weight: 500;
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.primary};
  text-wrap: pretty;
  overflow-wrap: anywhere;

  tr.row:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Sub = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const NarrowHead = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
`;

const ErrorsNote = styled.span`
  display: block;
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

function sourceLine(v: PrcVersionSummary, byId: ReadonlyMap<string, PrcVersionSummary>): string {
  const base = v.base_version_id ? byId.get(v.base_version_id) : undefined;
  if (v.source === 'copy' && base) return `${prcSourceLabel(v.source)} «${base.label}»`;
  return prcSourceLabel(v.source);
}

/** Объекты версии через запятую; без объектов — общая. Варианты условий — со
 *  своими объектами: «Склад; вариант «Тюмень»: БОВ, ДГП-2». */
function objectsLine(v: PrcVersionSummary): string {
  const main = v.objects.length === 0 ? PRC_COMMON_LABEL : v.objects.map((o) => o.name).join(', ');
  const variants = (v.variants ?? []).map(
    (x) =>
      `вариант «${x.name}»: ${x.objects.length > 0 ? x.objects.map((o) => o.name).join(', ') : 'объекты не выбраны'}`,
  );
  return [main, ...variants].join('; ');
}

export function VersionsTable({
  items,
  all,
}: {
  items: readonly PrcVersionSummary[];
  /** Все версии списка — чтобы назвать основу копии из другого раздела. */
  all?: readonly PrcVersionSummary[];
}) {
  const navigate = useNavigate();
  const byId = new Map((all ?? items).map((v) => [v.id, v]));
  const open = (id: string) => navigate(`/agents/econ/prc/${id}`);

  return (
    <ScrollRegion label="Версии расчёта ПРЦ">
      <Table>
        <thead>
          <tr>
            <th scope="col">Версия</th>
            <th scope="col">Объекты</th>
            <th scope="col">Статус</th>
            <th scope="col" className="num">
              Базовый год
            </th>
            <th scope="col" className="num">
              Моделей
            </th>
            <th scope="col">Автор</th>
            <th scope="col">Утверждена</th>
            <th scope="col">Действует с</th>
          </tr>
        </thead>
        <tbody>
          {items.map((v) => {
            const source = sourceLine(v, byId);
            const meta = [
              v.base_year ? `базовый год ${v.base_year}` : null,
              count(v.models_count, MODELS),
              v.errors_count > 0 ? `с ошибкой ${formatCount(v.errors_count)}` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            const people = [
              `автор: ${personName(v.created_by_name)}, ${formatDay(v.created_at)}`,
              v.approved_at
                ? `утверждена ${formatDay(v.approved_at)} (${personName(v.approved_by_name)})`
                : null,
              v.valid_from ? `действует с ${formatIsoDate(v.valid_from)}` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <tr
                key={v.id}
                className="row"
                onClick={() => {
                  if (window.getSelection()?.toString()) return;
                  open(v.id);
                }}
              >
                <td className="c-name">
                  <NarrowHead>
                    <NameLink to={`/agents/econ/prc/${v.id}`} onClick={(e) => e.stopPropagation()}>
                      {v.label}
                    </NameLink>
                    <span className="narrow-only">
                      <FleetChip $tone={prcStatusTone(v.status)}>
                        {prcStatusLabel(v.status)}
                      </FleetChip>
                    </span>
                  </NarrowHead>
                  <Sub>{source}</Sub>
                  <Sub className="narrow-only">{objectsLine(v)}</Sub>
                  <Sub className="narrow-only">{meta}</Sub>
                  <Sub className="narrow-only">{people}</Sub>
                </td>
                <td className="wide-only c-objects">{objectsLine(v)}</td>
                <td className="wide-only">
                  <FleetChip $tone={prcStatusTone(v.status)}>{prcStatusLabel(v.status)}</FleetChip>
                </td>
                <td className="wide-only num">{v.base_year ?? '—'}</td>
                <td className="wide-only num">
                  {formatCount(v.models_count)}
                  {v.errors_count > 0 && (
                    <ErrorsNote>с ошибкой {formatCount(v.errors_count)}</ErrorsNote>
                  )}
                </td>
                <td className="wide-only c-person">
                  {personName(v.created_by_name)}
                  <Sub>{formatDay(v.created_at)}</Sub>
                </td>
                <td className="wide-only c-person">
                  {v.approved_at ? (
                    <>
                      {formatDay(v.approved_at)}
                      <Sub>{personName(v.approved_by_name)}</Sub>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="wide-only">{formatIsoDate(v.valid_from)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </ScrollRegion>
  );
}
